import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { type PagedOptionsSource, usePagedOptions } from "@/hooks/use-paged-options";
import type { OptionItem } from "@/lib/list-query";
import { cn } from "@/lib/utils";

export interface LazyComboboxProps extends PagedOptionsSource {
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Resolves the selected option when it is on no loaded page — the usual case
   * for a value the form opened with. Skipped when `selectedLabel` is given.
   * Null means the option no longer exists: the selection is cleared.
   */
  fetchSelected?: (id: string) => Promise<OptionItem | null>;
  /** The selected option's label, when the caller already knows it. */
  selectedLabel?: string;
  /** False parks every request — for a source whose scope is not known yet. */
  enabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Label for the entry that clears the selection. Omit to require one. */
  clearLabel?: string;
  /** Id of the trigger button — what a visible `<Label htmlFor>` points at. */
  id?: string;
  /** Accessible name for the trigger, where no visible <Label> points at it. */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * A single-select dropdown over a list too long to ship whole: it fetches
 * nothing until it is opened, searches on the server, and loads the next page
 * as the list is scrolled.
 *
 * It is deliberately source-agnostic — give it a `fetchPage` and a `queryKey`
 * and it works against any module's paged list. `AgentCombobox` is the worked
 * example; a tool or knowledge-base picker is the same six lines.
 *
 * The one thing a paged picker has to get right is the selected value: the
 * option a form opens with is usually on no page that has been loaded, and
 * rendering the placeholder over a real selection reads as data loss. So the
 * label comes from the loaded pages when it is there, and from `selectedLabel`
 * or a one-row `fetchSelected` when it is not.
 */
export function LazyCombobox({
  value,
  onChange,
  queryKey,
  fetchPage,
  fetchSelected,
  selectedLabel,
  enabled = true,
  placeholder = "Select an option",
  searchPlaceholder = "Search…",
  emptyMessage = "No results found.",
  clearLabel,
  id,
  ariaLabel,
  disabled,
  className,
}: LazyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const { options, isFetching, hasNextPage, isError, listRef, endRef } = usePagedOptions({
    queryKey,
    fetchPage,
    search: searchInput,
    // Nothing is requested until the menu is opened: that is the lazy part, and
    // it is why a screen with six of these costs six requests, not six lists.
    enabled: enabled && open,
  });

  const loadedLabel = options.find((option) => option.id === value)?.name;

  const { data: resolved } = useQuery({
    queryKey: [...queryKey, "lazy-selected", value],
    queryFn: () => fetchSelected!(value!),
    enabled: enabled && !!value && !selectedLabel && !loadedLabel && !!fetchSelected,
    staleTime: 5 * 60 * 1000,
  });

  // The selected option is gone (an agent deleted since the form loaded): clear
  // it, so the placeholder shows and the form's own "required" check applies,
  // rather than keep a dead id to submit.
  useEffect(() => {
    if (value && resolved === null) onChange(null);
  }, [value, resolved, onChange]);

  const label = selectedLabel ?? loadedLabel ?? resolved?.name;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reopening starts from page one, so it should start from no search too.
        if (!next) setSearchInput("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled || !enabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className={cn("truncate", !label && "text-muted-foreground")}>
            {label ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      {/*
        `max-h` off Radix's own measurement, not a fixed number: without it the
        menu keeps its full height in a short viewport and simply renders past
        the bottom of the screen, where the last rows cannot be reached at all.
        The column then gives the search box its natural height and hands the
        rest to the list, which is what scrolls.
      */}
      <PopoverContent
        className="flex max-h-[min(20rem,var(--radix-popover-content-available-height))] w-[--radix-popover-trigger-width] flex-col overflow-hidden p-0"
        align="start"
        collisionPadding={8}
      >
        {/* The server does the searching, so cmdk must not also filter the page
            it was handed — it would hide rows that matched. */}
        <Command shouldFilter={false} className="flex min-h-0 flex-1 flex-col">
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchInput}
            onValueChange={setSearchInput}
          />
          <CommandList
            ref={listRef}
            // `max-h-none`: the popover is already bounded, so the list takes
            // what is left rather than imposing a second, fixed ceiling of its
            // own. `scroll-pb-12` keeps the arrow keys from parking a row under
            // the loading bar below — it is what `scrollIntoView` leaves free at
            // the bottom, and the bar is shorter than that.
            className="max-h-none min-h-0 flex-1 scroll-pb-12"
          >
            {/* Not gated on the search term: clearing the selection is how a
                user backs out of a filter, and hiding it behind an empty
                search box makes that impossible without first clearing the
                box. `shouldFilter={false}` means cmdk leaves it alone. */}
            {clearLabel && (
              <CommandGroup>
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value ? "opacity-0" : "opacity-100")} />
                  {clearLabel}
                </CommandItem>
              </CommandGroup>
            )}

            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === option.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{option.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            {/* What `usePagedOptions` watches for. It sits after the rows so
                that reaching it means the end of what has been loaded.

                Its height is the room the loading bar needs. Without it the
                list is already at its maximum scroll when the last row is
                reached, `scroll-pb-12` has nothing left to give, and the row
                the arrow keys just landed on ends up underneath the bar. Once
                the bar is actually there it holds that room open itself, so
                this gets out of the way — left at full height it would stack
                with the bar and show as a blank gap under the last row. */}
            {hasNextPage && (
              <div ref={endRef} aria-hidden className={isFetching ? "h-px" : "h-12"} />
            )}

            {/* Pinned to the bottom of the visible list rather than just placed
                after the last row. The arrow keys move one row at a time and
                scroll the highlight only as far as its own bottom edge, so a
                loader sitting below the fold — which is where the next page is
                asked for — is never actually on screen: the rows arrive first
                and it reads as a freeze. Sticky, it is visible wherever the
                list happens to be scrolled, and it still lands in its natural
                place at the end once the list is scrolled all the way down. */}
            {isFetching && (
              <div
                className={cn(
                  "sticky bottom-0 flex items-center justify-center gap-2 bg-popover py-3 text-sm text-muted-foreground",
                  options.length > 0 && "border-t",
                )}
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            )}

            {isError && !isFetching && (
              <div className="py-6 text-center text-sm text-destructive">
                Couldn't load the list. Try again.
              </div>
            )}

            {!isFetching && !isError && options.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
